import { useState, useRef, useEffect, useCallback } from 'react';
import { Head } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { route } from '../../utils/route';
import { getCSRFToken } from '../../utils/csrf';

interface Session {
    session_id: string;
    status: string;
    summary: string | null;
    created_at: string;
}

interface Message {
    id: number;
    role: 'user' | 'assistant' | 'hitl' | 'system';
    content: string;
    created_at: string;
    hitl?: {
        uuid: string;
        summary: string;
        approver: 'user' | 'admin';
        status: 'pending' | 'approved' | 'rejected' | 'processing';
    };
}

interface Props {
    sessions: Session[];
    isAnonymous: boolean;
    userName: string | null;
}

export default function SupportChat({ sessions: initialSessions, isAnonymous, userName }: Props) {
    const [sessions] = useState<Session[]>(initialSessions);
    const [activeSessionId, setActiveSessionId] = useState<string | null>(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('s') || null;
    });
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const [isResolved, setIsResolved] = useState(false);
    const [isVerified, setIsVerified] = useState(!isAnonymous);
    const [showList, setShowList] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return !params.get('s') && !isAnonymous;
    });
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const abortRef = useRef<AbortController | null>(null);

    const updateUrl = useCallback((sessionId: string | null) => {
        const url = new URL(window.location.href);
        if (sessionId) {
            url.searchParams.set('s', sessionId);
        } else {
            url.searchParams.delete('s');
        }
        // Keep Inertia's page state on this entry — replacing it with {} makes
        // a later back/forward visit resolve a component named `undefined`.
        window.history.replaceState(window.history.state, '', url.toString());
    }, []);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    // Constrain layout to 100dvh
    useEffect(() => {
        const main = document.querySelector('main') as HTMLElement | null;
        const root = main?.parentElement as HTMLElement | null;
        if (!main || !root) return;

        const prevRoot = { height: root.style.height, minHeight: root.style.minHeight, overflow: root.style.overflow };
        const prevMain = { overflow: main.style.overflow, display: main.style.display, flexDirection: main.style.flexDirection, minHeight: main.style.minHeight };

        root.style.height = '100dvh';
        root.style.minHeight = '0';
        root.style.overflow = 'hidden';

        const prevBodyOverflow = document.body.style.overflow;
        const prevHtmlOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        main.style.display = 'flex';
        main.style.flexDirection = 'column';
        main.style.overflow = 'hidden';
        main.style.minHeight = '0';

        return () => {
            root.style.height = prevRoot.height;
            root.style.minHeight = prevRoot.minHeight;
            root.style.overflow = prevRoot.overflow;
            document.body.style.overflow = prevBodyOverflow;
            document.documentElement.style.overflow = prevHtmlOverflow;
            main.style.overflow = prevMain.overflow;
            main.style.display = prevMain.display;
            main.style.flexDirection = prevMain.flexDirection;
            main.style.minHeight = prevMain.minHeight;
        };
    }, []);

    // Auto-load session from URL on mount
    useEffect(() => {
        if (activeSessionId) {
            loadSession(activeSessionId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-focus input after streaming ends
    useEffect(() => {
        if (!isStreaming) {
            inputRef.current?.focus();
        }
    }, [isStreaming]);

    const loadSession = useCallback(async (sessionId: string) => {
        setActiveSessionId(sessionId);
        setShowList(false);
        updateUrl(sessionId);

        try {
            const res = await fetch(route('support.messages', { sessionId }), {
                credentials: 'include',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            if (!res.ok) return;
            const data = await res.json();
            setMessages(data.messages);
            setIsResolved(data.resolved ?? false);
            if (data.user_id) {
                setIsVerified(true);
            }
        } catch {
            // silently fail
        }
    }, [updateUrl]);

    // Poll for verification status (anonymous only).
    // Once verified, keep polling messages until the agent's post-verification
    // response arrives (it's generated async by ProcessVerificationResponseJob).
    const [waitingForPostVerify, setWaitingForPostVerify] = useState(false);
    const preVerifyCountRef = useRef(0);

    useEffect(() => {
        if (isVerified || !activeSessionId) return;

        const interval = setInterval(async () => {
            try {
                const res = await fetch(
                    route('support.verify-status', { sessionId: activeSessionId }),
                    { credentials: 'include' }
                );
                if (!res.ok) return;
                const data = await res.json();

                if (data.verified) {
                    setIsVerified(true);
                    preVerifyCountRef.current = messages.length;
                    setWaitingForPostVerify(true);
                }
            } catch {
                // silently retry next interval
            }
        }, 3000);

        return () => clearInterval(interval);
    }, [isVerified, activeSessionId, messages.length]);

    // After verification, poll messages until the agent's response appears
    useEffect(() => {
        if (!waitingForPostVerify || !activeSessionId) return;

        let attempts = 0;
        const interval = setInterval(async () => {
            attempts++;
            try {
                const res = await fetch(route('support.messages', { sessionId: activeSessionId }), {
                    credentials: 'include',
                    headers: { 'X-Requested-With': 'XMLHttpRequest' },
                });
                if (!res.ok) return;
                const data = await res.json();

                if (data.messages.length > preVerifyCountRef.current) {
                    setMessages(data.messages);
                    setWaitingForPostVerify(false);
                }
            } catch {
                // retry
            }

            // Stop after ~30s (10 attempts × 3s)
            if (attempts >= 10) {
                setWaitingForPostVerify(false);
            }
        }, 3000);

        return () => clearInterval(interval);
    }, [waitingForPostVerify, activeSessionId]);

    const startNewSession = useCallback(async () => {
        setActiveSessionId(null);
        setMessages([]);
        setShowList(false);
        setIsResolved(false);
        setIsVerified(!isAnonymous);
        updateUrl(null);
        inputRef.current?.focus();
    }, [updateUrl, isAnonymous]);

    const sendMessage = useCallback(async () => {
        const text = input.trim();
        if (!text || isStreaming) return;

        setInput('');

        const userMsg: Message = {
            id: Date.now(),
            role: 'user',
            content: text,
            created_at: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
        };
        setMessages(prev => [...prev, userMsg]);

        const assistantId = Date.now() + 1;
        setMessages(prev => [...prev, {
            id: assistantId,
            role: 'assistant',
            content: '',
            created_at: '',
        }]);

        setIsStreaming(true);

        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const res = await fetch(route('support.stream'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': getCSRFToken(),
                },
                credentials: 'include',
                body: JSON.stringify({
                    message: text,
                    session_id: activeSessionId,
                }),
                signal: controller.signal,
            });

            if (!res.ok || !res.body) {
                throw new Error('Stream failed');
            }

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const event = JSON.parse(line.slice(6));

                        if (event.type === 'chunk') {
                            setMessages(prev => {
                                const updated = [...prev];
                                const last = updated[updated.length - 1];
                                if (last && last.role === 'assistant') {
                                    updated[updated.length - 1] = {
                                        ...last,
                                        content: last.content + event.delta,
                                    };
                                }
                                return updated;
                            });
                        } else if (event.type === 'new_turn') {
                            setMessages(prev => [
                                ...prev,
                                { id: Date.now() + 10, role: 'assistant', content: '', created_at: '' },
                            ]);
                        } else if (event.type === 'hitl_request') {
                            setMessages(prev => {
                                const updated = [...prev];
                                const last = updated[updated.length - 1];
                                if (last && last.role === 'assistant' && !last.content) {
                                    updated.pop();
                                }
                                return [...updated, {
                                    id: Date.now() + 2,
                                    role: 'hitl' as const,
                                    content: event.summary,
                                    created_at: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
                                    hitl: {
                                        uuid: event.hitl_uuid,
                                        summary: event.summary,
                                        approver: event.approver,
                                        status: 'pending' as const,
                                    },
                                }];
                            });
                            if (event.session_id && event.session_id !== activeSessionId) {
                                setActiveSessionId(event.session_id);
                                updateUrl(event.session_id);
                            }
                        } else if (event.type === 'done') {
                            if (event.session_id && event.session_id !== activeSessionId) {
                                setActiveSessionId(event.session_id);
                                updateUrl(event.session_id);
                            }
                            if (event.resolved) {
                                setIsResolved(true);
                            }
                        } else if (event.type === 'error') {
                            setMessages(prev => {
                                const updated = [...prev];
                                const last = updated[updated.length - 1];
                                if (last && last.role === 'assistant' && !last.content) {
                                    updated[updated.length - 1] = {
                                        ...last,
                                        content: event.error || 'Ocurrió un error. Intentá de nuevo.',
                                    };
                                }
                                return updated;
                            });
                        }
                    } catch {
                        // skip malformed JSON
                    }
                }
            }
        } catch (err: unknown) {
            if (err instanceof DOMException && err.name === 'AbortError') return;
            setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === 'assistant' && !last.content) {
                    updated[updated.length - 1] = {
                        ...last,
                        content: 'Error de conexión. Intentá de nuevo.',
                    };
                }
                return updated;
            });
        } finally {
            setIsStreaming(false);
            abortRef.current = null;
        }
    }, [input, isStreaming, activeSessionId, updateUrl]);

    const handleHitlDecision = useCallback(async (uuid: string, approved: boolean) => {
        setMessages(prev => prev.map(msg =>
            msg.hitl?.uuid === uuid
                ? { ...msg, hitl: { ...msg.hitl, status: 'processing' as const } }
                : msg
        ));

        const assistantId = Date.now() + 3;
        setMessages(prev => [...prev, {
            id: assistantId,
            role: 'assistant',
            content: '',
            created_at: '',
        }]);

        setIsStreaming(true);

        const endpoint = approved
            ? route('support.hitl.approve', { uuid })
            : route('support.hitl.reject', { uuid });

        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': getCSRFToken(),
                },
                credentials: 'include',
            });

            if (!res.ok || !res.body) {
                throw new Error('HITL request failed');
            }

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const event = JSON.parse(line.slice(6));
                        if (event.type === 'chunk') {
                            setMessages(prev => {
                                const updated = [...prev];
                                const last = updated[updated.length - 1];
                                if (last && last.role === 'assistant') {
                                    updated[updated.length - 1] = {
                                        ...last,
                                        content: last.content + event.delta,
                                    };
                                }
                                return updated;
                            });
                        } else if (event.type === 'error') {
                            setMessages(prev => {
                                const updated = [...prev];
                                const last = updated[updated.length - 1];
                                if (last && last.role === 'assistant' && !last.content) {
                                    updated[updated.length - 1] = {
                                        ...last,
                                        content: event.error || 'Error al procesar.',
                                    };
                                }
                                return updated;
                            });
                        }
                    } catch {
                        // skip
                    }
                }
            }

            setMessages(prev => prev.map(msg =>
                msg.hitl?.uuid === uuid
                    ? { ...msg, hitl: { ...msg.hitl, status: (approved ? 'approved' : 'rejected') as const } }
                    : msg
            ));
        } catch {
            setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === 'assistant' && !last.content) {
                    updated[updated.length - 1] = {
                        ...last,
                        content: 'Error de conexión. Intentá de nuevo.',
                    };
                }
                return updated;
            });
        } finally {
            setIsStreaming(false);
        }
    }, []);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    }, [sendMessage]);

    const hasActiveChat = !showList;

    return (
        <MainLayout>
            <Head>
                <title>Soporte</title>
                <meta name="robots" content="noindex" />
            </Head>

            <div className="mx-auto w-full max-w-6xl flex flex-1 min-h-0">

                    {/* Sidebar — session list (hidden for anonymous) */}
                    {!isAnonymous && (
                        <div className={`${hasActiveChat ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 min-h-0 border-r border-gray-200 bg-gray-50`}>
                            <div className="flex items-center justify-between p-4 border-b border-gray-200">
                                <h2 className="font-semibold text-gray-800" style={{ fontSize: 18 }}>Soporte</h2>
                                <button
                                    type="button"
                                    onClick={startNewSession}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity bg-transparent border-none cursor-pointer"
                                    style={{ background: '#1EAA25' }}
                                >
                                    <i className="fa fa-plus" style={{ fontSize: 11 }} />
                                    Nueva consulta
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto">
                                {sessions.length === 0 && (
                                    <div className="p-6 text-center text-gray-400 text-sm">
                                        No tenés consultas previas.<br />
                                        Escribinos tu consulta.
                                    </div>
                                )}
                                {sessions.map(s => (
                                    <button
                                        key={s.session_id}
                                        type="button"
                                        onClick={() => loadSession(s.session_id)}
                                        className={`w-full text-left p-3 border-b border-gray-100 hover:bg-white transition-colors bg-transparent border-none cursor-pointer ${
                                            activeSessionId === s.session_id ? 'bg-white border-l-2' : ''
                                        }`}
                                        style={activeSessionId === s.session_id ? { borderLeftColor: '#1EAA25' } : {}}
                                    >
                                        <div className="font-medium text-gray-800 text-sm truncate">{s.summary || 'Consulta'}</div>
                                        <div className="text-xs text-gray-400 mt-0.5">{s.created_at}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Chat panel */}
                    <div className={`${hasActiveChat || isAnonymous ? 'flex' : 'hidden md:flex'} flex-col flex-1 min-h-0 bg-white`}>
                        {/* Chat header (mobile back button) */}
                        {hasActiveChat && !isAnonymous && (
                            <div className="flex items-center gap-3 p-3 border-b border-gray-200 md:hidden">
                                <button
                                    type="button"
                                    onClick={() => setShowList(true)}
                                    className="p-1 text-gray-500 hover:text-gray-800 bg-transparent border-none cursor-pointer"
                                >
                                    <i className="fa fa-arrow-left" />
                                </button>
                                <span className="font-medium text-gray-800 text-sm truncate">Soporte</span>
                            </div>
                        )}

                        {/* Anonymous badge */}
                        {isAnonymous && !isVerified && (
                            <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-700 flex items-center gap-2">
                                <span>&#128274;</span>
                                <span>Modo anónimo — el agente te va a pedir verificar tu identidad para consultas de pedidos.</span>
                            </div>
                        )}

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-4" style={{ paddingBottom: 8 }}>
                            {messages.length === 0 && !isStreaming && (
                                <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
                                    <i className="fa fa-comment-dots" style={{ fontSize: 40, marginBottom: 16, opacity: 0.3 }} />
                                    <p className="text-sm">
                                        Escribí tu consulta y te ayudamos al instante.
                                    </p>
                                </div>
                            )}
                            {messages.map(msg => (
                                msg.role === 'hitl' && msg.hitl ? (
                                    <HITLWidget
                                        key={msg.id}
                                        hitl={msg.hitl}
                                        isStreaming={isStreaming}
                                        onDecision={handleHitlDecision}
                                    />
                                ) : msg.role === 'system' ? (
                                    <SystemMessage key={msg.id} content={msg.content} />
                                ) : (
                                    <MessageBubble key={msg.id} message={msg} isStreaming={isStreaming && msg === messages[messages.length - 1] && msg.role === 'assistant'} />
                                )
                            ))}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input bar or resolved banner */}
                        {isResolved ? (
                            <div className="border-t border-gray-200 p-3 text-center">
                                <p className="text-sm text-gray-500">--- Conversación finalizada ---</p>
                                <button
                                    type="button"
                                    onClick={startNewSession}
                                    className="mt-1.5 text-sm font-medium border-none bg-transparent cursor-pointer"
                                    style={{ color: '#1EAA25' }}
                                >
                                    Iniciar nueva consulta
                                </button>
                            </div>
                        ) : (
                            <div className="border-t border-gray-200 p-2 px-3">
                                <div className="flex items-center gap-2">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={input}
                                        onChange={e => setInput(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Escribí tu mensaje..."
                                        disabled={isStreaming}
                                        className="flex-1 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:border-green-500 disabled:opacity-50"
                                        style={{ height: 36 }}
                                    />
                                    <button
                                        type="button"
                                        onClick={sendMessage}
                                        disabled={isStreaming || !input.trim()}
                                        className="flex items-center justify-center rounded-lg text-white disabled:opacity-40 transition-opacity border-none cursor-pointer"
                                        style={{ background: '#1EAA25', width: 36, height: 36, flexShrink: 0 }}
                                    >
                                        <i className="fa fa-paper-plane" style={{ fontSize: 13 }} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
            </div>
        </MainLayout>
    );
}

/* --- System Message (verification confirmation, etc.) --- */

function SystemMessage({ content }: { content: string }) {
    return (
        <div className="flex justify-center mb-3">
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm text-green-800 font-medium">
                {content}
            </div>
        </div>
    );
}

/* --- HITL Approval Widget --- */

function HITLWidget({ hitl, isStreaming, onDecision }: {
    hitl: NonNullable<Message['hitl']>;
    isStreaming: boolean;
    onDecision: (uuid: string, approved: boolean) => void;
}) {
    const isPending = hitl.status === 'pending';
    const isProcessing = hitl.status === 'processing';

    return (
        <div className="flex mb-3 justify-start">
            <div className="max-w-[85%] md:max-w-[70%] rounded-2xl px-4 py-3 text-sm border border-amber-200 bg-amber-50">
                <div className="flex items-start gap-2 mb-2">
                    <span style={{ fontSize: 18 }}>&#9888;&#65039;</span>
                    <div>
                        <div className="font-medium text-gray-800">{hitl.summary}</div>
                        {hitl.approver === 'user' && isPending && (
                            <div className="text-xs text-gray-500 mt-0.5">Se necesita tu confirmacion para continuar.</div>
                        )}
                        {hitl.approver === 'admin' && isPending && (
                            <div className="text-xs text-gray-500 mt-0.5">Esperando aprobacion de un operador.</div>
                        )}
                    </div>
                </div>

                {hitl.approver === 'user' && isPending && (
                    <div className="flex gap-2 mt-2">
                        <button
                            type="button"
                            onClick={() => onDecision(hitl.uuid, true)}
                            disabled={isStreaming}
                            className="flex-1 px-3 py-1.5 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity border-none cursor-pointer disabled:opacity-40"
                            style={{ background: '#1EAA25' }}
                        >
                            Confirmar
                        </button>
                        <button
                            type="button"
                            onClick={() => onDecision(hitl.uuid, false)}
                            disabled={isStreaming}
                            className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-200 transition-colors bg-gray-100 border-none cursor-pointer disabled:opacity-40"
                        >
                            Cancelar
                        </button>
                    </div>
                )}

                {isProcessing && (
                    <div className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                        <TypingIndicator /> Procesando...
                    </div>
                )}

                {hitl.status === 'approved' && (
                    <div className="text-xs text-green-600 mt-2 font-medium">Confirmado</div>
                )}

                {hitl.status === 'rejected' && (
                    <div className="text-xs text-red-600 mt-2 font-medium">Cancelado</div>
                )}
            </div>
        </div>
    );
}

/* --- Message Bubble --- */

function MessageBubble({ message, isStreaming }: { message: Message; isStreaming: boolean }) {
    const isUser = message.role === 'user';

    return (
        <div className={`flex mb-3 ${isUser ? 'justify-end' : 'justify-start'}`}>
            <div
                className={`max-w-[85%] md:max-w-[70%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    isUser
                        ? 'bg-green-600 text-white rounded-br-md'
                        : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}
            >
                {message.content ? (
                    <MessageContent content={message.content} isUser={isUser} />
                ) : isStreaming ? (
                    <TypingIndicator />
                ) : null}
            </div>
        </div>
    );
}

/* --- Simple Markdown-ish Renderer --- */

function MessageContent({ content, isUser }: { content: string; isUser: boolean }) {
    const html = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener" class="underline">$1</a>')
        .replace(/\n/g, '<br/>');

    return (
        <span
            dangerouslySetInnerHTML={{ __html: html }}
            className={isUser ? '' : 'prose-sm'}
        />
    );
}

/* --- Typing Indicator --- */

function TypingIndicator() {
    return (
        <span className="inline-flex gap-1 items-center" style={{ padding: '4px 0' }}>
            {[0, 1, 2].map(i => (
                <span
                    key={i}
                    className="rounded-full bg-gray-400"
                    style={{
                        width: 6,
                        height: 6,
                        animation: `typing 1.2s infinite ${i * 0.2}s`,
                    }}
                />
            ))}
            <style>{`
                @keyframes typing {
                    0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
                    30% { opacity: 1; transform: translateY(-3px); }
                }
            `}</style>
        </span>
    );
}
